import React, { useMemo, useContext } from 'react';
import { SharedSelectionDocumentManagerState, DummySharedSelectionDocumentSpec, makeSharedSelectionDocumentSpec } from 'app/models/rtc/selection/documentSpec';
import {
  ISharedSelectionAreaUpdater,
  useSharedSelectionUpdater,
} from 'app/containers/rtc/useSharedSelectionUpdater';
import { selectionReducer } from 'app/models/rtc/selection/reducer';
import { ParticipantSelections } from 'app/models/rtc/selection/constants';
import { throwingFunction } from 'app/utils/throwingMethods';
import useDocumentManager from 'app/containers/rtc/DocumentProvider/useDocumentManager';
import { Stream } from '@engoo/realtime-protocol/stream';
import { useRtcState } from 'app/containers/rtc/RtcStateContext';
import SelectionActionHandler from 'app/models/rtc/selection/actionHandler';
import { LazyDocumentManager } from 'app/services/rtc/sync/documentmanagers/lazy';
import { usePeers } from 'app/containers/rtc/PeersContext';

export interface ISharedSelectionDocumentContext extends Pick<
  SharedSelectionDocumentManagerState,
  'metadata' | 'isBlocked'
> {
  readonly selections: ParticipantSelections;
  readonly addArea: () => ISharedSelectionAreaUpdater;
}

export const SharedSelectionDocumentContext = React.createContext<
  ISharedSelectionDocumentContext
>({
  selections: selectionReducer().selections,
  addArea: throwingFunction,
  metadata: undefined,
  isBlocked: true,
});

export const useSharedSelectionContext = () => useContext(SharedSelectionDocumentContext);

export const SharedSelectionDocumentProvider: React.FC = ({ children }) => {
  const { caller } = useRtcState();
  const peers = usePeers();

  const spec = useMemo(() => {
    if (caller?.id) {
      return makeSharedSelectionDocumentSpec({
        managerType: LazyDocumentManager,
        actionHandler: new SelectionActionHandler(caller.id),
      });
    } else {
      return DummySharedSelectionDocumentSpec;
    }
  }, [caller]);

  const store = useDocumentManager(Stream.Selection, spec);

  const {
    manager,
    metadata,
    documentState = selectionReducer(),
    isBlocked,
  } = store;

  const { addArea } = useSharedSelectionUpdater(
    manager, isBlocked,
  );

  const selections: ParticipantSelections = useMemo(() => {
    return documentState.selections.filter(
      (selection) => peers.has(selection.user) || selection.user === caller?.id,
    );
  }, [documentState.selections, caller, peers]);

  const contextValue: ISharedSelectionDocumentContext = useMemo(() => ({
    selections,
    metadata,
    isBlocked,
    addArea,
  }), [
    selections, metadata, isBlocked, addArea,
  ]);

  return (
    <SharedSelectionDocumentContext.Provider value={contextValue}>
      {children}
    </SharedSelectionDocumentContext.Provider>
  );
};
